// unit-c-frontend/src/components/SocialMedia/MediaUploadButton.jsx
import React, { useRef, useState } from 'react';
import { FiUploadCloud } from 'react-icons/fi';
import mediaService from '../../services/mediaService';
import { notify } from '../CustomToast';

const ACCEPT_MAP = {
    image: 'image/jpeg,image/png,image/gif,image/webp',
    video: 'video/mp4,video/webm,video/quicktime',
    audio: 'audio/mpeg,audio/wav,audio/ogg,audio/aac',
};

const MAX_BYTES = 20 * 1024 * 1024; // 20 MB

const MediaUploadButton = ({ activeTab, onUploaded }) => {
    const inputRef = useRef(null);
    const [uploadingFiles, setUploadingFiles] = useState([]);

    const setFileProgress = (fileId, patch) => {
        setUploadingFiles(prev =>
            prev.map(f => f.id === fileId ? { ...f, ...patch } : f)
        );
    };

    const handleFiles = async (files) => {
        const fileArray = Array.from(files);
        const oversized = fileArray.filter(f => f.size > MAX_BYTES);
        if (oversized.length > 0) {
            notify('error', `Files exceed 20 MB limit: ${oversized.map(f => f.name).join(', ')}`);
            return;
        }

        const newEntries = fileArray.map(f => ({
            id: Math.random().toString(36).slice(2),
            name: f.name,
            percent: 0,
            status: 'uploading',
        }));
        setUploadingFiles(prev => [...prev, ...newEntries]);

        for (let i = 0; i < fileArray.length; i++) {
            const file = fileArray[i];
            const entry = newEntries[i];

            try {
                const res = await mediaService.upload(file, activeTab, (pct) => {
                    setFileProgress(entry.id, { percent: pct });
                });
                const uploaded = res?.data ?? res;
                setFileProgress(entry.id, { percent: 100, status: 'done' });
                onUploaded(uploaded);
                // Remove from progress list after 1.5s
                setTimeout(() => {
                    setUploadingFiles(prev => prev.filter(f => f.id !== entry.id));
                }, 1500);
            } catch (err) {
                setFileProgress(entry.id, { status: 'error' });
                notify('error', err?.message || 'Upload failed');
            }
        }

        // Reset input so same file can be re-selected
        if (inputRef.current) inputRef.current.value = '';
    };

    return (
        <div>
            <input
                ref={inputRef}
                type="file"
                accept={ACCEPT_MAP[activeTab]}
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleFiles(e.target.files)}
            />
            <button
                className="mp-upload-btn"
                onClick={() => inputRef.current?.click()}
            >
                <FiUploadCloud size={18} />
                Upload Media
            </button>

            {uploadingFiles.length > 0 && (
                <div className="mp-progress-list">
                    {uploadingFiles.map(f => (
                        <div key={f.id} className="mp-progress-item">
                            <div className="mp-progress-header">
                                <span className="mp-progress-name">{f.name}</span>
                                <span className="mp-progress-pct">
                                    {f.status === 'error' ? 'Error' : `${f.percent}%`}
                                </span>
                            </div>
                            <div className="mp-progress-bar-wrap">
                                <div
                                    className={`mp-progress-bar ${f.status === 'done' ? 'done' : f.status === 'error' ? 'error' : ''}`}
                                    style={{ width: `${f.percent}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MediaUploadButton;
